<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useSongStore } from '../../stores/songStore'
import { useBandStore } from '../../stores/bandStore'
import { usePresenceStore } from '../../stores/presenceStore'
import { useUiStore } from '../../stores/uiStore'
import type { SongPartDto, MixDto } from '../../dtos'
import draggable from 'vuedraggable'
import ChatPanel from './ChatPanel.vue'
import { parsePerformance } from '../../utils/performance'

const emit = defineEmits(['back', 'view-part', 'view-mix'])

const songStore = useSongStore()
const bandStore = useBandStore()
const presenceStore = usePresenceStore()
const uiStore = useUiStore()
const song = computed(() => songStore.currentSong)
const hasSongNotes = computed(() => {
  if (!song.value) return false
  return song.value.tuning || song.value.capo || song.value.notes || (song.value.chords && song.value.chords.length > 0)
})
const parts = computed({
  get: () => songStore.currentSong?.parts || [],
  set: (val) => {
    if (songStore.currentSong) {
      songStore.currentSong.parts = val
    }
  }
})
const loading = computed(() => songStore.detailLoading)

const props = defineProps<{
  songId: number
}>()

onMounted(async () => {
  await songStore.fetchSongDetails(props.songId)
})

const masterPerformance = computed(() => {
  return parts.value.filter(p => p.isActive).map(p => ({
    name: p.name,
    lines: parsePerformance(p.performance || null)
  }))
})

const maxPartWidth = computed(() => {
  const partWidths = masterPerformance.value.map(part => {
    // Measure name (adding a small buffer for the tracking/uppercase style)
    const nameWidth = part.name.length + 4

    // Measure longest rendered line in this specific part
    const linesWidth = part.lines.reduce((max, line) => {
      const w = line.segments.reduce((s, seg) => s + seg.text.length, 0)
      return Math.max(max, w)
    }, 0)

    return Math.max(nameWidth, linesWidth)
  })

  // Return the greatest width found across all parts, with a minimum floor
  return Math.max(...partWidths, 20)
})

const newPartName = ref('')
const addingPart = ref(false)
const duplicatingId = ref<number | null>(null)
const showDeleteConfirm = ref(false)
const partToDelete = ref<SongPartDto | null>(null)

// Mix Logic
const mixes = computed(() => songStore.currentSong?.mixes || [])
const newMixTitle = ref('')
const addingMix = ref(false)
const showDeleteMixConfirm = ref(false)
const mixToDelete = ref<MixDto | null>(null)

const handleAddMix = async () => {
  if (!newMixTitle.value || !song.value) return
  addingMix.value = true
  try {
    await songStore.addMix(newMixTitle.value, song.value.id)
    newMixTitle.value = ''
  } finally {
    addingMix.value = false
  }
}

const confirmDeleteMix = async (mix: MixDto) => {
  const isLocked = await presenceStore.checkConcurrency('Mix', mix.id)
  if (isLocked) {
    uiStore.triggerConcurrencyError('Cannot delete this mix while other members are active in it.')
    return
  }
  mixToDelete.value = mix
  showDeleteMixConfirm.value = true
}

const handleDeleteMix = async () => {
  if (!mixToDelete.value) return
  try {
    await songStore.deleteMix(mixToDelete.value.id)
    showDeleteMixConfirm.value = false
    mixToDelete.value = null
  } catch (error) {
    console.error(error)
  }
}

// Edit Song Logic
const showEditDialog = ref(false)
const editTitle = ref('')
const editArtist = ref('')
const editKey = ref('')
const editTimeSignature = ref('')
const editBPM = ref<number | null>(null)
const editTuning = ref('')
const editCapo = ref<number | null>(null)
const editNotes = ref('')
const editChords = ref<{ name: string; voicing: string }[]>([])
const isUpdatingSong = ref(false)

const handleExport = () => {
  if (!song.value) return
  const exportData: Record<string, unknown> = {
    title: song.value.title,
    artist: song.value.artist || undefined,
    key: song.value.key || undefined,
    bpm: song.value.bPM || undefined,
    tuning: song.value.tuning || undefined,
    capo: song.value.capo || undefined,
    notes: song.value.notes || undefined,
    chords: song.value.chords?.length ? song.value.chords.map(c => ({ name: c.name, voicing: c.voicing })) : undefined,
    parts: parts.value.map(p => ({
      name: p.name,
      sequenceNumber: p.sequenceNumber,
      performance: p.performance || undefined
    }))
  }
  // Remove undefined keys
  const clean = JSON.parse(JSON.stringify(exportData))
  const blob = new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${song.value.title.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase()}.json`
  a.click()
  URL.revokeObjectURL(url)
}

const openEditDialog = () => {
  if (!song.value) return
  editTitle.value = song.value.title
  editArtist.value = song.value.artist || ''
  editKey.value = song.value.key || ''
  editTimeSignature.value = song.value.timeSignature || ''
  editBPM.value = song.value.bPM || null
  editTuning.value = song.value.tuning || ''
  editCapo.value = song.value.capo || null
  editNotes.value = song.value.notes || ''
  editChords.value = song.value.chords ? song.value.chords.map(c => ({ ...c })) : []
  showEditDialog.value = true
}

const handleUpdateSong = async () => {
  if (!song.value || !editTitle.value) return
  isUpdatingSong.value = true
  try {
    await songStore.updateSong(song.value.id, {
      title: editTitle.value,
      artist: editArtist.value,
      key: editKey.value,
      timeSignature: editTimeSignature.value || undefined,
      bPM: editBPM.value || undefined,
      tuning: editTuning.value || undefined,
      capo: editCapo.value || undefined,
      notes: editNotes.value || undefined,
      chords: editChords.value.length > 0 ? editChords.value.filter(c => c.name) : undefined
    })
    showEditDialog.value = false
  } catch (error) {
    console.error(error)
  } finally {
    isUpdatingSong.value = false
  }
}

const addChord = () => {
  editChords.value.push({ name: '', voicing: '' })
}

const removeChord = (index: number) => {
  editChords.value.splice(index, 1)
}

const handleAddPart = async () => {
  if (!newPartName.value || !song.value) return
  addingPart.value = true
  try {
    await songStore.addSongPart(newPartName.value, song.value.id)
    newPartName.value = ''
  } finally {
    addingPart.value = false
  }
}

const confirmDelete = async (part: SongPartDto) => {
  if (!song.value) return
  // Parts are locked by parent Song presence
  const isLocked = await presenceStore.checkConcurrency('Song', song.value.id)
  if (isLocked) {
    uiStore.triggerConcurrencyError('Cannot delete song parts while other members are active in this song.')
    return
  }
  partToDelete.value = part
  showDeleteConfirm.value = true
}

const handleDelete = async () => {
  if (!partToDelete.value || !song.value) return
  try {
    await songStore.deleteSongPart(partToDelete.value.id)
    showDeleteConfirm.value = false
    partToDelete.value = null
  } catch (error) {
    console.error(error)
  }
}

const handleToggleActive = async (partId: number) => {
  await songStore.toggleSongPartActive(partId)
}

const showDuplicateConfirm = ref(false)
const partToDuplicate = ref<SongPartDto | null>(null)

const confirmDuplicate = (part: SongPartDto) => {
  partToDuplicate.value = part
  showDuplicateConfirm.value = true
}

const handleDuplicate = async () => {
  if (!partToDuplicate.value) return
  duplicatingId.value = partToDuplicate.value.id
  try {
    await songStore.duplicateSongPart(partToDuplicate.value.id)
    showDuplicateConfirm.value = false
    partToDuplicate.value = null
  } catch (error) {
    console.error(error)
  } finally {
    duplicatingId.value = null
  }
}

const handleReorder = async () => {
  if (!song.value) return
  const partIds = parts.value.map(p => p.id)
  try {
    await songStore.reorderSongParts(song.value.id, partIds)
  } catch (error) {
    console.error(error)
  }
}

const showPrintView = ref(false)
const printScale = ref(100)
const printColumns = ref(1)
const printIncludeInfo = ref(true)

const handlePrint = () => {
  showPrintView.value = true
}

const handleBackFromPrint = () => {
  showPrintView.value = false
}

// Lock page scroll while the print preview overlay is open to prevent
// showing two vertical scrollbars (page scrollbar + overlay scrollbar).
let savedScrollY = 0
const lockPageScroll = () => {
  savedScrollY = window.scrollY
  document.body.style.setProperty('position', 'fixed', 'important')
  document.body.style.setProperty('top', `-${savedScrollY}px`, 'important')
  document.body.style.setProperty('left', '0', 'important')
  document.body.style.setProperty('right', '0', 'important')
  document.body.style.setProperty('overflow', 'hidden', 'important')
  document.documentElement.style.setProperty('overflow', 'hidden', 'important')
}
const unlockPageScroll = () => {
  document.body.style.removeProperty('position')
  document.body.style.removeProperty('top')
  document.body.style.removeProperty('left')
  document.body.style.removeProperty('right')
  document.body.style.removeProperty('overflow')
  document.documentElement.style.removeProperty('overflow')
  window.scrollTo(0, savedScrollY)
}
watch(showPrintView, (open) => {
  if (open) lockPageScroll()
  else unlockPageScroll()
})
onUnmounted(() => {
  if (showPrintView.value) unlockPageScroll()
})

const doPrint = () => {
  if (!song.value) return
  const printEl = document.getElementById('song-print-content')
  if (!printEl) return
  const scale = printScale.value / 100
  const cols = printColumns.value
  const printWindow = window.open('', '_blank', 'width=800,height=600')
  if (!printWindow) return
  printWindow.document.write(`<!DOCTYPE html><html><head><title>${song.value.title}</title>
<style>
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 2rem; color: #1e293b; transform: scale(${scale}); transform-origin: top left; width: ${100 / scale}%; }
  .chord-line { font-weight: bold; line-height: 1; height: 1.25rem; white-space: pre; font-size: 14px; }
  .lyric-line { line-height: 1.3; white-space: pre; font-size: 14px; color: #334155; }
  .part-name { font-size: 13px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.2em; margin-bottom: 12px; }
  .part-block { margin-bottom: 2rem; break-inside: avoid; }
  .line-pair { margin-bottom: 4px; }
  .meta { font-size: 14px; color: #475569; }
  .meta strong { color: #1e293b; }
  h1 { margin: 0 0 4px 0; font-size: 24px; }
  .artist { font-size: 18px; color: #475569; margin-bottom: 16px; }
  .song-parts { font-family: monospace; columns: ${cols}; column-gap: 3rem; }
</style></head><body>${printEl.innerHTML}</body></html>`)
  printWindow.document.close()
  printWindow.focus()
  printWindow.print()
  printWindow.close()
}

const handleBack = () => {
  songStore.currentSong = null
  emit('back')
}
</script>

<template>
  <div class="space-y-6 p-1 lg:p-8 bg-slate-200 min-h-full rounded-xl">
    <!-- Loading State -->
    <div v-if="loading || !song" class="py-20 text-center flex flex-col items-center justify-center rd-card-inner border-slate-300">
      <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="6" fill="transparent"
          animationDuration=".5s" aria-label="Loading Song" />
      <p class="mt-4 text-sm text-slate-400 font-medium animate-pulse">Loading Song Details...</p>
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Header -->
      <div class="flex items-center gap-4">
        <Button icon="pi pi-arrow-left" text rounded severity="secondary" @click="handleBack" />
        <div>
          <p class="rd-label leading-none mb-1">Song</p>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl font-black text-slate-900 leading-tight">{{ song.title }}</h1>
            <Button v-if="bandStore.canEditSongs" icon="pi pi-pencil" text rounded severity="secondary" size="small" class="opacity-40 hover:opacity-100" v-tooltip.top="'Edit Song Details'" @click="openEditDialog" />
            <Button icon="pi pi-download" text rounded severity="secondary" size="small" class="opacity-40 hover:opacity-100" v-tooltip.top="'Export Song as JSON'" @click="handleExport" />
            <Button icon="pi pi-print" text rounded severity="secondary" size="small" class="opacity-40 hover:opacity-100" v-tooltip.top="'Print Song Sheet'" @click="handlePrint" />
          </div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="grid grid-cols-2 gap-4">
        <div class="p-4 rd-card-inner border-slate-300 text-center">
          <p class="rd-label mb-1">Artist</p>
          <p class="text-sm font-medium text-slate-700">{{ song.artist || 'Not set' }}</p>
        </div>
        <div class="p-4 rd-card-inner border-slate-300 text-center">
          <p class="rd-label mb-1">Key / BPM</p>
          <p class="text-sm font-medium text-slate-700">{{ song.key || '-' }} / {{ song.bPM || '-' }}</p>
        </div>
      </div>

      <!-- Song Notes Section -->
      <div v-if="hasSongNotes" class="mt-8 rd-card-inner border-slate-300 overflow-hidden">
        <Accordion class="song-parts-accordion">
          <AccordionPanel value="notes">
            <AccordionHeader class="p-6">
              <span class="text-sm font-bold text-slate-600">Song Notes</span>
            </AccordionHeader>
            <AccordionContent class="px-6 pb-6">
              <div class="flex flex-col gap-3 text-sm">
                <div v-if="song!.tuning" class="flex items-center gap-2">
                  <span class="font-bold text-slate-500 uppercase text-[10px] tracking-wider w-16">Tuning</span>
                  <span class="font-mono text-slate-700">{{ song!.tuning }}</span>
                </div>
                <div v-if="song!.capo" class="flex items-center gap-2">
                  <span class="font-bold text-slate-500 uppercase text-[10px] tracking-wider w-16">Capo</span>
                  <span class="text-slate-700">Fret {{ song!.capo }}</span>
                </div>
                <div v-if="song!.chords && song!.chords.length > 0">
                  <span class="font-bold text-slate-500 uppercase text-[10px] tracking-wider">Chords</span>
                  <div class="flex flex-col gap-1 mt-2">
                    <div v-for="chord in song!.chords" :key="chord.name" class="flex items-center gap-2">
                      <span class="font-bold text-teal-600 w-16">{{ chord.name }}</span>
                      <span class="font-mono text-slate-500 text-xs">{{ chord.voicing }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="song!.notes" class="flex items-start gap-2">
                  <span class="font-bold text-slate-500 uppercase text-[10px] tracking-wider w-16 shrink-0">Notes</span>
                  <span class="text-slate-700">{{ song!.notes }}</span>
                </div>
              </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>

      <!-- Song Parts Section -->
      <div class="mt-8 rd-card-inner border-slate-300 overflow-hidden">
        <Accordion multiple class="song-parts-accordion">
          <AccordionPanel value="parts">
            <AccordionHeader class="p-6">
              <span class="text-sm font-bold text-slate-600">Song Parts</span>
              <span class="ml-auto text-[10px] font-bold text-slate-300" style="order: 2">{{ parts.length }} Total</span>
            </AccordionHeader>
            <AccordionContent class="px-6 pb-6">
            <!-- Add Part Input -->
            <div v-if="bandStore.canEditSongs" class="flex gap-2 mb-4">
              <InputText v-model="newPartName" placeholder="New part (e.g. Verse 1)..." class="flex-1 text-sm bg-slate-50 border-slate-300" size="small" @keyup.enter="handleAddPart" />
              <Button icon="pi pi-plus" size="small" severity="success" :loading="addingPart" @click="handleAddPart" />
            </div>

            <!-- Parts List with Draggable -->
            <draggable
              v-model="parts"
              item-key="id"
              handle=".drag-handle"
              @end="handleReorder"
              class="flex flex-col gap-2"
            >
              <template #item="{element: part}">
                <div
                     :class="['group flex items-center justify-between p-4 rounded-2xl border transition-all cursor-pointer',
                       'bg-slate-50/50 border-slate-400 hover:border-teal-600 hover:shadow-md hover:shadow-teal-500/5']"
                     @click="$emit('view-part', part)">
                  <div class="flex items-center gap-4">
                    <div v-if="bandStore.canEditSongs" class="drag-handle cursor-move p-1 text-slate-400 hover:text-slate-600 transition-colors" v-tooltip.top="'Drag to reorder'">
                      <i class="pi pi-bars"></i>
                    </div>
                    <div class="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-slate-400 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors border border-slate-300 shadow-sm">
                      {{ part.sequenceNumber }}
                    </div>
                    <span :class="['text-sm font-bold transition-colors text-slate-700 group-hover:text-slate-900', !part.isActive && 'line-through']">{{ part.name }}</span>
                  </div>
                  <div v-if="bandStore.canEditSongs" class="flex items-center gap-1">
                    <Checkbox :modelValue="part.isActive" :binary="true"
                              class="mr-1"
                              v-tooltip.top="part.isActive ? 'Active' : 'Inactive'"
                              @click.stop
                              @update:modelValue="handleToggleActive(part.id)" />
                    <Button icon="pi pi-copy" severity="secondary" text rounded size="small"
                            class="opacity-60 hover:opacity-100 transition-opacity"
                            :loading="duplicatingId === part.id"
                            v-tooltip.top="'Duplicate part'"
                            @click.stop="confirmDuplicate(part)" />
                    <Button icon="pi pi-trash" severity="danger" text rounded size="small"
                            class="opacity-60 hover:opacity-100 transition-opacity"
                            v-tooltip.top="'Delete part'"
                            @click.stop="confirmDelete(part)" />
                  </div>
                </div>
              </template>
            </draggable>

            <div v-if="parts.length === 0" class="py-12 text-center border-2 border-dashed border-slate-300 rounded-3xl bg-slate-50/30">
              <div class="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 text-slate-200">
                <i class="pi pi-th-large text-2xl"></i>
              </div>
              <p class="text-xs text-slate-400 italic">No parts defined for this song</p>
            </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>

      <!-- Master Performance (Song Sheet) -->
      <div v-if="masterPerformance.some(p => p.lines.length > 0)" class="mt-8 p-6 rd-card-inner border-slate-300 overflow-x-auto">
        <div :style="{ columns: `${maxPartWidth}ch auto`, columnGap: '3rem' }">
          <div v-for="(part, pIndex) in masterPerformance" :key="pIndex"
               class="mb-10 break-inside-avoid">
            <div v-if="part.lines.length > 0">
              <p class="text-sm font-bold text-teal-600 uppercase tracking-[0.2em] mb-4 border-b border-slate-300 pb-1">{{ part.name }}</p>
              <div class="font-mono">
                <div v-for="(line, lIndex) in part.lines" :key="lIndex"
                     :class="line.segments.length === 0 ? 'h-3' : 'leading-tight whitespace-pre text-[16px]'">
                  <span v-for="(seg, sIdx) in line.segments" :key="sIdx"
                        :class="seg.type === 'chord' ? 'text-teal-600 font-bold' : 'text-slate-700'">{{ seg.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mixes Section -->
      <div class="mt-8 p-6 rd-card-inner border-slate-300">
        <div class="flex items-center justify-between mb-4">
          <p class="rd-label">Mixes</p>
          <span class="text-[10px] font-bold text-slate-300">{{ mixes.length }} Total</span>
        </div>

        <!-- Add Mix Input -->
        <div v-if="bandStore.canEditMixes" class="flex gap-2 mb-6">
          <InputText v-model="newMixTitle" placeholder="Mix title (e.g. Rough Demo)..." class="flex-1 text-sm bg-slate-50 border-slate-300" size="small" @keyup.enter="handleAddMix" />
          <Button icon="pi pi-plus" size="small" severity="success" :loading="addingMix" @click="handleAddMix" />
        </div>

        <!-- Mixes List -->
        <div v-if="mixes.length > 0" class="flex flex-col gap-2">
          <div v-for="mix in mixes" :key="mix.id" 
               class="group flex items-center justify-between p-4 bg-slate-50/50 rounded-2xl border border-slate-400 hover:border-teal-600 hover:shadow-md hover:shadow-teal-500/5 transition-all cursor-pointer"
               @click="$emit('view-mix', mix)">
            <div class="flex items-center gap-4">
              <div class="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-teal-500 border border-slate-300 shadow-sm">
                <i class="pi pi-headphones text-lg"></i>
              </div>
              <div>
                <p class="text-sm font-bold text-slate-900">{{ mix.title }}</p>
              </div>
            </div>
            <Button v-if="bandStore.canEditMixes" icon="pi pi-trash" severity="danger" text rounded size="small"
                    class="opacity-60 hover:opacity-100 transition-opacity"
                    v-tooltip.top="'Delete mix'"
                    @click.stop="confirmDeleteMix(mix)" />
          </div>
        </div>
        <div v-else class="py-12 text-center border-2 border-dashed border-slate-300 rounded-3xl bg-slate-50/30">
          <div class="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 text-slate-200">
            <i class="pi pi-headphones text-2xl"></i>
          </div>
          <p class="text-xs text-slate-400 italic">No mixes yet</p>
        </div>
      </div>

      <!-- Song Chat -->
      <div v-if="song" class="mt-8 pt-4 rd-divider">
        <ChatPanel :band-id="song.bandId" :song-id="song.id" label="Chat" />
      </div>
    </template>

    <!-- Mix Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteMixConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will permanently delete the mix <strong>"{{ mixToDelete?.title }}"</strong> and all its associated tracks and audio clips.</p>
        
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete Mix" severity="danger" class="w-full py-3 font-bold" @click="handleDeleteMix" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteMixConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Edit Song Details Dialog -->
    <Dialog v-model:visible="showEditDialog" modal header="Edit Song Details" :style="{ width: '30rem' }">
      <div class="flex flex-col gap-4 py-2">
        <div class="flex flex-col gap-2">
          <label for="editTitle" class="font-semibold text-sm">Song Title</label>
          <InputText id="editTitle" v-model="editTitle" class="w-full" />
        </div>
        <div class="flex flex-col gap-2">
          <label for="editArtist" class="font-semibold text-sm">Artist</label>
          <InputText id="editArtist" v-model="editArtist" class="w-full" />
        </div>
        <div class="grid grid-cols-3 gap-4">
          <div class="flex flex-col gap-2">
            <label for="editKey" class="font-semibold text-sm">Key</label>
            <InputText id="editKey" v-model="editKey" class="w-full" placeholder="e.g. Am" />
          </div>
          <div class="flex flex-col gap-2">
            <label for="editTimeSignature" class="font-semibold text-sm">Time Sig.</label>
            <InputText id="editTimeSignature" v-model="editTimeSignature" class="w-full" placeholder="e.g. 4/4" />
          </div>
          <div class="flex flex-col gap-2">
            <label for="editBPM" class="font-semibold text-sm">BPM</label>
            <InputNumber id="editBPM" v-model="editBPM" class="w-full" :useGrouping="false" fluid />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-2">
            <label for="editTuning" class="font-semibold text-sm">Tuning</label>
            <InputText id="editTuning" v-model="editTuning" class="w-full" placeholder="e.g. Standard, Drop D" />
          </div>
          <div class="flex flex-col gap-2">
            <label for="editCapo" class="font-semibold text-sm">Capo</label>
            <InputNumber id="editCapo" v-model="editCapo" class="w-full" :useGrouping="false" :min="0" :max="12" fluid />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label for="editNotes" class="font-semibold text-sm">Notes</label>
          <Textarea id="editNotes" v-model="editNotes" class="w-full" rows="3" placeholder="Song notes..." />
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <label class="font-semibold text-sm">Chords</label>
            <Button icon="pi pi-plus" size="small" severity="secondary" text rounded v-tooltip.top="'Add chord'" @click="addChord" />
          </div>
          <div v-if="editChords.length > 0" class="flex flex-col gap-2 max-h-40 overflow-y-auto">
            <div v-for="(chord, idx) in editChords" :key="idx" class="flex items-center gap-2">
              <InputText v-model="chord.name" placeholder="Chord name" class="flex-1 !text-xs" size="small" />
              <InputText v-model="chord.voicing" placeholder="Voicing" class="flex-1 !text-xs" size="small" />
              <Button icon="pi pi-times" severity="danger" text rounded size="small" class="opacity-60 hover:opacity-100" @click="removeChord(idx)" />
            </div>
          </div>
          <p v-else class="text-xs text-slate-400 italic">No chords added</p>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button type="button" label="Cancel" severity="secondary" text @click="showEditDialog = false" />
          <Button type="button" label="Save Changes" :loading="isUpdatingSong" @click="handleUpdateSong" />
        </div>
      </div>
    </Dialog>

    <!-- Print Preview -->
    <div v-if="showPrintView && song" class="fixed inset-0 z-[200] bg-white overflow-auto">
      <div class="p-4 border-b border-slate-200 bg-slate-50 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <!-- Row 1 on phone: Back only -->
        <div class="sm:contents">
          <Button icon="pi pi-arrow-left" label="Back" text severity="secondary" @click="handleBackFromPrint" />
        </div>
        <!-- Row 2 on phone: Scale + Columns + Song Info + Print -->
        <div class="flex items-start gap-3 sm:gap-4 sm:contents">
          <div class="flex flex-col-reverse items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <label class="text-xs font-semibold text-slate-500">Scale</label>
            <InputNumber v-model="printScale" :min="30" :max="200" :step="10" suffix="%" :useGrouping="false" class="!w-14 sm:!w-24 [&_input]:!w-full [&_input]:!min-w-0" size="small" fluid />
          </div>
          <div class="flex flex-col-reverse items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <label class="text-xs font-semibold text-slate-500">Columns</label>
            <InputNumber v-model="printColumns" :min="1" :max="4" :useGrouping="false" class="!w-10 sm:!w-20 [&_input]:!w-full [&_input]:!min-w-0" size="small" fluid />
          </div>
          <div class="flex flex-col-reverse items-center gap-1 sm:flex-row sm:items-center sm:gap-2">
            <label for="printInfo" class="text-xs font-semibold text-slate-500 cursor-pointer mt-1 sm:mt-0">Song Info</label>
            <div class="flex items-center h-8 sm:h-auto">
              <Checkbox v-model="printIncludeInfo" :binary="true" inputId="printInfo" />
            </div>
          </div>
          <Button icon="pi pi-print" label="Print" severity="primary" @click="doPrint" />
        </div>
      </div>
      <div class="px-8 pb-8 pt-6" :style="{ transform: `scale(${printScale / 100})`, transformOrigin: 'top left', width: `${100 / (printScale / 100)}%` }">
        <div id="song-print-content">
          <h1 class="text-3xl font-black text-slate-900 mb-1">{{ song.title }}</h1>
          <p v-if="song.artist" class="text-lg text-slate-600 mb-4 artist">{{ song.artist }}</p>

          <template v-if="printIncludeInfo">
            <div class="flex flex-wrap gap-x-8 gap-y-1 text-sm text-slate-600 mb-2 meta">
              <span v-if="song.key"><strong>Key:</strong> {{ song.key }}</span>
              <span v-if="song.bPM"><strong>BPM:</strong> {{ song.bPM }}</span>
              <span v-if="song.tuning"><strong>Tuning:</strong> {{ song.tuning }}</span>
              <span v-if="song.capo"><strong>Capo:</strong> Fret {{ song.capo }}</span>
            </div>

            <div v-if="song.chords && song.chords.length > 0" class="text-sm text-slate-600 mb-2 meta">
              <strong>Chords:</strong>
              <span v-for="(chord, i) in song.chords" :key="chord.name">
                {{ chord.name }}<span v-if="chord.voicing" class="text-slate-400"> ({{ chord.voicing }})</span><span v-if="i < song.chords.length - 1">, </span>
              </span>
            </div>

            <p v-if="song.notes" class="text-sm text-slate-600 mb-6 meta"><strong>Notes:</strong> {{ song.notes }}</p>
          </template>

          <div v-if="masterPerformance.some(p => p.lines.length > 0)" class="song-parts" :style="{ columns: printColumns, columnGap: '3rem' }">
            <div v-for="(part, pIndex) in masterPerformance" :key="pIndex" class="mb-8 break-inside-avoid part-block">
              <div v-if="part.lines.length > 0">
                <p class="text-sm font-bold text-slate-900 uppercase tracking-[0.2em] mb-3 part-name">{{ part.name }}</p>
                <div class="font-mono">
                  <div v-for="(line, lIndex) in part.lines" :key="lIndex"
                       :class="line.segments.length === 0 ? 'h-3' : 'leading-tight whitespace-pre text-[14px] lyric-line'">
                    <span v-for="(seg, sIdx) in line.segments" :key="sIdx"
                          :class="seg.type === 'chord' ? 'font-bold chord-line' : 'text-slate-700'">{{ seg.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-slate-400 italic">No song sheet content to print.</p>
        </div>
      </div>
    </div>

    <!-- Part Duplicate Confirmation Dialog -->
    <Dialog v-model:visible="showDuplicateConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-teal-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-copy text-teal-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Duplicate Part?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will create a copy of <strong>"{{ partToDuplicate?.name }}"</strong>.</p>

        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Duplicate" severity="success" class="w-full py-3 font-bold" :loading="duplicatingId === partToDuplicate?.id" @click="handleDuplicate" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDuplicateConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Part Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will permanently delete the song part <strong>"{{ partToDelete?.name }}"</strong> and its performance data.</p>
        
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete Part" severity="danger" class="w-full py-3 font-bold" @click="handleDelete" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteConfirm = false" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

